import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, ModalDialog, ModalClose, Typography, Box, Stack, Checkbox, Snackbar, Input } from '@mui/joy';
import { Search } from '@mui/icons-material';
import { useCreateMealPlanEntryMutation } from '../api';
import { useGetFavoritesQuery } from '@/features/recipes/favoritesApi';
import { localDateToString } from '@/utils/datetime';
import { statusCodeToMessage } from '@/utils/errors';
import { setShowAllCategories } from '../state';
import { SelectableButtonBar } from '@/components/SelectableButtonBar';
import { SortMenu } from '@/components/SortMenu';
import { ControlsGrid } from '@/components/ControlsGrid';
import { RecipeFiltersButton } from '@/features/recipes/components/RecipeFiltersDrawer';
import { newRecipeFilters, newSort } from '@/features/recipes/schema';
import { sortColumnOptions, DEFAULT_SORT_COLUMN, sortRecipes, filterRecipes, computeRecipeBounds } from '@/features/recipes/filters';
import { MealTypePicker } from '@/features/recipes/components/MealTypePicker';
import { RecipePickerList } from './RecipePickerList';
import { IngredientPickerList } from './IngredientPickerList';
import { EventPickerList } from './EventPickerList';

const CONTENT_TABS = [
  { key: 'recipes', label: 'Recipes' },
  { key: 'ingredients', label: 'Ingredients' },
  { key: 'events', label: 'Events' },
];

export function AddMealModal({ open, onClose, date, dayName, dayNumber, recipes, householdId, isEditable, mealType }) {
  const navigate = useNavigate();
  const [selectedMealType, setSelectedMealType] = useState(mealType || 'breakfast');
  const [contentTab, setContentTab] = useState('recipes');
  const [errorMessage, setErrorMessage] = useState(null);
  const showAllCategories = useSelector((state) => state.mealPlan.showAllCategories);
  const dispatch = useDispatch();
  const [createMutation, createMutationResult] = useCreateMealPlanEntryMutation();
  const favorites = useGetFavoritesQuery({ householdId });

  const [recipeFilters, setRecipeFilters] = useState(newRecipeFilters());
  const [sort, setSort] = useState(newSort());

  const favoriteIds = useMemo(() => new Set(favorites.data?.data || []), [favorites.data]);

  const recipeBounds = useMemo(() => computeRecipeBounds(recipes), [recipes]);

  const effectiveRecipeFilters = useMemo(() => ({
    ...recipeFilters,
    categories: showAllCategories ? [] : [selectedMealType],
  }), [recipeFilters, showAllCategories, selectedMealType]);

  const filteredRecipes = useMemo(
    () => sortRecipes(filterRecipes(recipes, effectiveRecipeFilters, favoriteIds), sort),
    [recipes, effectiveRecipeFilters, favoriteIds, sort]
  );

  useEffect(() => {
    if (open && mealType) {
      setSelectedMealType(mealType);
    }
  }, [open, mealType]);

  useEffect(() => {
    if (!open) {
      setContentTab('recipes');
    }
  }, [open]);

  const handleShowAllCategoriesChange = (checked) => {
    dispatch(setShowAllCategories(checked));
  };

  const dateString = localDateToString(date);

  const formatDateLabel = () => {
    return `${dayName}, ${date.toLocaleDateString('en-US', { month: 'short' })} ${dayNumber}`;
  };

  const handleAddRecipe = async (recipeId) => {
    if (!isEditable) return;

    try {
      await createMutation({
        householdId,
        data: {
          date: dateString,
          entry_type: 'recipe',
          recipe_id: recipeId,
          meal_type: selectedMealType,
        },
      });
      onClose();
    } catch (error) {
      console.error('Failed to add recipe to meal plan:', error);
    }
  };

  const handleClose = () => {
    setSelectedMealType(mealType || 'breakfast');
    setContentTab('recipes');
    setRecipeFilters(newRecipeFilters());
    setSort(newSort());
    onClose();
  };

  // Hands off to the full recipe-creation form rather than a cut-down inline
  // one, so it gets the same tabs/validation as creating a recipe anywhere
  // else. The plan date/meal type ride along as query params so
  // CreateRecipePage can slot the new recipe into this exact meal once it's
  // saved, then land back here instead of on the new recipe's own page.
  const handleAddNewRecipe = () => {
    const params = new URLSearchParams({ planDate: dateString, planMealType: selectedMealType });
    handleClose();
    navigate(`/h/${householdId}/recipes/create?${params.toString()}`);
  };

  async function handleAddIngredient({ ingredient_id, amount, unit }) {
    const result = await createMutation({
      householdId,
      data: {
        date: dateString,
        entry_type: 'ingredient',
        ingredient: { ingredient_id, amount, unit },
        meal_type: selectedMealType,
      },
    });

    if (result.error) {
      setErrorMessage(statusCodeToMessage(result.error.status));
      return;
    }

    handleClose();
  }

  async function handleAddEvent(eventId) {
    const result = await createMutation({
      householdId,
      data: {
        date: dateString,
        entry_type: 'event',
        event_id: eventId,
        meal_type: selectedMealType,
      },
    });

    if (result.error) {
      setErrorMessage(statusCodeToMessage(result.error.status));
      return;
    }

    handleClose();
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ maxWidth: '1300px', width: '90%', height: '90vh', display: 'flex', flexDirection: 'column', pb: 0 }}>
        <ModalClose />

        <Typography
          level="h4"
          component="div"
          sx={{ mb: 0, pr: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
        >
          Add to {formatDateLabel()} as
          <MealTypePicker variant="header" value={selectedMealType} onChange={setSelectedMealType} />
        </Typography>

        <Box sx={{ mb: 1, mt: 0.5 }}>
          <SelectableButtonBar
            options={CONTENT_TABS.map((tab) => ({
              key: tab.key,
              label: tab.label,
              selected: contentTab === tab.key,
              onClick: () => setContentTab(tab.key),
            }))}
          />
        </Box>

        {contentTab === 'recipes' && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={1.5}
            // mt matches the content Box below, so this search box lines up
            // with the one the ingredients tab renders inside that Box
            sx={{ mt: 0.5, mb: 1 }}
          >
            <Input
              placeholder="Search recipes..."
              value={recipeFilters.search}
              onChange={(e) => setRecipeFilters({ ...recipeFilters, search: e.target.value })}
              startDecorator={<Search />}
              sx={{ flex: 1, minWidth: 0 }}
              autoFocus
            />

            <ControlsGrid>
              <SortMenu
                sort={sort}
                setSort={setSort}
                columnOptions={sortColumnOptions}
                defaultColumn={DEFAULT_SORT_COLUMN}
                sx={{ width: '100%' }}
              />
              <RecipeFiltersButton
                filters={effectiveRecipeFilters}
                setFilters={setRecipeFilters}
                bounds={recipeBounds}
                resultsCount={filteredRecipes.length}
                sx={{ width: '100%' }}
              />
            </ControlsGrid>
          </Stack>
        )}

        {contentTab === 'recipes' && (
          <Checkbox
            checked={showAllCategories}
            onChange={(e) => handleShowAllCategoriesChange(e.target.checked)}
            label="Show recipes from all categories"
            size="sm"
            sx={{ alignSelf: 'flex-start', mb: 0.5, mt: -0.5 }}
          />
        )}

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', mt: 0.5, pb: 2 }}>
          {contentTab === 'recipes' && (
            <RecipePickerList
              recipes={filteredRecipes}
              favoriteIds={favoriteIds}
              onAdd={handleAddRecipe}
              onAddNewRecipe={handleAddNewRecipe}
            />
          )}

          {contentTab === 'ingredients' && (
            <IngredientPickerList onAdd={handleAddIngredient} isSaving={createMutationResult.isLoading} />
          )}

          {contentTab === 'events' && (
            <EventPickerList householdId={householdId} onAdd={handleAddEvent} />
          )}
        </Box>

        <Snackbar
          color="danger"
          variant="soft"
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={!!errorMessage}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Snackbar>
      </ModalDialog>
    </Modal>
  );
}
